/* eslint-disable jsdoc/require-param */
import React, { useEffect, useState } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { useCompany } from "../../../contexts/CompanyContext";
import { supabase } from "../../../supabase";
import ConfirmationModal from "../../shared/ConfirmationModal";
/**
 * ManageDeal Component
 *
 * This component renders a form for creating or editing a deal. It includes
 * fields for deal name, description, supplier, product type, and deal type.
 * The component supports both creating a new deal and editing an existing one,
 * with the appropriate form data pre-filled in edit mode.
 *
 * Props:
 * - editing: A boolean indicating whether the form is in edit mode.
 * - dealProp: An object containing the deal properties for pre-filling the form in edit mode.
 * - onClose: A function to close the form dialog.
 *
 * State:
 * - formData: An object holding the form input data.
 * - dealId: The ID of the deal being edited.
 * - successMessage: A success message to display upon successful save.
 * - isSnackbarOpen: A boolean indicating if the success snackbar is open.
 * - saving: A boolean indicating if the form is currently being saved.
 * - suppliersForSelector: A list of suppliers filtered based on the selected product type.
 * - error: An error message to display if form submission fails.
 *
 * Effects:
 * - Loads deal data into the form when editing.
 * - Updates the list of available suppliers when the product type changes.
 *
 * Handlers:
 * - setUpSuppliers: Filters the suppliers based on the selected product type.
 * - handleChange: Updates formData state when input fields change.
 * - handleSubmit: Validates and submits form data to create or update a deal.
 */
export default function ManageDeal({ editing, dealProp, onClose, supplierId }) {
  const { productTypes, dealTypes } = useCompany();

  const [formData, setFormData] = useState({
    deal_name: "",
    deal_description: "",
    suppliers_master_id: supplierId,
    refs_product_type_id: null,
    refs_deal_type_id: null,
  });
  const [dealId, setDealId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);

  useEffect(() => {
    if (editing) {
      setDealId(dealProp.id);
      setFormData(dealProp);
    }
  }, []);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError(null);
    setSaving(true); // Add loading state for UX

    // Validate required fields
    const requiredFields = {
      deal_name: "Deal Name",
      deal_description: "Deal Description",
      suppliers_master_id: "Supplier",
      refs_product_type_id: "Product Type",
      refs_deal_type_id: "Deal Type",
    };

    const missingFields = Object.keys(requiredFields).filter(
      key => !formData[key]
    );

    if (missingFields.length > 0) {
      setError(
        `Please fill in the following required fields: ${missingFields
          .map(key => requiredFields[key])
          .join(", ")}`
      );
      setSaving(false);
      return;
    }

    const dataToSubmit = {
      deal_name: formData.deal_name,
      deal_description: formData.deal_description,
      suppliers_master_id: formData.suppliers_master_id,
      refs_product_type_id: formData.refs_product_type_id,
      refs_deal_type_id: formData.refs_deal_type_id,
    };

    try {
      const { queryError } = editing
        ? await supabase
            .schema(process.env.REACT_APP_SB_SCHEMA)
            .from("deals_master")
            .update(dataToSubmit)
            .eq("id", dealId)
        : await supabase
            .schema(process.env.REACT_APP_SB_SCHEMA)
            .from("deals_master")
            .insert(dataToSubmit);

      if (queryError) {
        throw new Error(queryError.message);
      }

      // Provide success feedback
      setSuccessMessage(
        editing ? "Deal updated successfully!" : "Deal created successfully!"
      );
      setIsSnackbarOpen(true);
      setTimeout(() => {
        setSaving(false);
        onClose();
      }, 1500);
    } catch (error2) {
      setError(
        `Failed to ${editing ? "update" : "create"} deal: ${error2.message}`
      );
    }
  };

  const handleDelete = async () => {
    setConfirmModal(false);
    supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("deals_master")
      .delete()
      .eq("id", dealId)
      .then(() => {
        setSuccessMessage("Deal deleted successfully!");
        setIsSnackbarOpen(true);
        setTimeout(() => {
          setSaving(false);
          onClose();
        }, 1500);
      })
      .catch(errorInstance => {
        setError(`Failed to delete deal: ${errorInstance.message}`);
      });
  };

  return (
    <Box className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h5"
            className="mb-6 text-center font-bold text-gray-800"
          >
            {editing ? "Edit Deal" : "Create Deal"}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Name"
            name="deal_name"
            value={formData.deal_name}
            onChange={handleChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="deal_description"
            value={formData.deal_description}
            onChange={handleChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel
            style={{
              fontSize: "14px",
            }}
          >
            Select Deal Type
          </InputLabel>
        </Grid>
        <Grid item xs={12}>
          <FormControl variant="outlined" size="medium" fullWidth>
            <Select
              fullWidth
              name="refs_deal_type_id"
              value={formData.refs_deal_type_id}
              onChange={handleChange}
              variant="outlined"
            >
              <MenuItem value="" disabled>
                Select Deal Type
              </MenuItem>
              {dealTypes.map(dealType => (
                <MenuItem value={dealType.id}>
                  {dealType.deal_type_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <InputLabel
            style={{
              fontSize: "14px",
            }}
          >
            Select Product Type
          </InputLabel>
        </Grid>
        <Grid item xs={12}>
          <FormControl variant="outlined" size="medium" fullWidth>
            <Select
              fullWidth
              name="refs_product_type_id"
              value={formData.refs_product_type_id}
              onChange={handleChange}
              variant="outlined"
            >
              <MenuItem value="" disabled>
                Select Product Type
              </MenuItem>
              {productTypes.map(productType => (
                <MenuItem value={productType.id}>{productType.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          {editing && (
            <Button
              style={{ maxWidth: 180, marginRight: 24 }}
              variant="contained"
              onClick={() => setConfirmModal(true)}
              className="mt-4 bg-blue-600 hover:bg-blue-700"
              color="primary"
              disabled={saving}
            >
              Delete
            </Button>
          )}
          <Button
            style={{ maxWidth: 180, marginRight: 24 }}
            variant="contained"
            color="primary"
            onClick={onClose}
            className="mt-4 bg-blue-600 hover:bg-blue-700"
            disabled={saving}
          >
            Close
          </Button>
          <Button
            disabled={saving}
            style={{ maxWidth: 180 }}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            className="mt-4 bg-blue-600 hover:bg-blue-700"
          >
            {saving ? "Saving..." : editing ? "Save" : "Create"}
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setIsSnackbarOpen(false)}
      >
        <Alert severity="success" onClose={() => setIsSnackbarOpen(false)}>
          {successMessage}
        </Alert>
      </Snackbar>
      <ConfirmationModal
        onConfirm={handleDelete}
        onClose={() => setConfirmModal(false)}
        open={confirmModal}
      />
    </Box>
  );
}
